import { createStyles, Theme } from "@material-ui/core";
import { CSSProperties } from "react";

/** Shared range-game palette (emerald accent, desaturated for UI chrome). */
export const RANGE_GAME_COLORS = {
  accent: "#2e7d32",
  bg: "#f4faf6",
  badgeSurface: "#e3f2e6",
  badgeText: "#1b5e20",
  badgeBorder: "rgba(27, 94, 32, 0.22)",
  badgeDot: "#43a047",
};

/** Game menu header wash for standard (non-range) games — not used in game lists. */
export const LIVE_GAME_MENU_COLORS = {
  backgroundColor: "#f9faff",
  backgroundImage: "linear-gradient(180deg, #f9faff 0%, #f6f8ff 100%)",
};

/** Matches DialogToolbar bottom edge — separates header band from menu list. */
export const GAME_MENU_HEADER_BORDER = "1px solid rgba(226, 232, 240, 0.92)";

/** Copyable game-code chip used on Current Games cards and Game Menu. */
export const gameCodeCapsuleStyles = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  height: 24,
  padding: "0 9px",
  margin: 0,
  border: "1px solid rgba(15, 23, 42, 0.12)",
  borderRadius: 100,
  backgroundColor: "rgba(15, 23, 42, 0.04)",
  color: "rgba(15, 23, 42, 0.82)",
  fontSize: "0.875rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  lineHeight: 1,
  fontFamily: "inherit",
  cursor: "pointer",
  boxSizing: "border-box" as const,
  textTransform: "uppercase" as const,
  appearance: "none" as const,
  WebkitAppearance: "none" as const,
  "&:hover": {
    backgroundColor: "rgba(15, 23, 42, 0.07)",
  },
  "&:focus": {
    outline: "2px solid rgba(25, 118, 210, 0.28)",
    outlineOffset: 1,
  },
};

export const rangeGameDetailsStyles = (theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
      minWidth: 0,
    },
    titleRow: {
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "center",
      columnGap: theme.spacing(1),
      lineHeight: 1.35,
      marginBottom: theme.spacing(1),
      minWidth: 0,
    },
    title: {
      fontWeight: 600,
      fontSize: "1rem",
      color: theme.palette.text.primary,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    codeCapsule: gameCodeCapsuleStyles,
    detailLine: {
      display: "block",
      fontSize: "0.9375rem",
      lineHeight: 1.55,
      color: "rgba(0, 0, 0, 0.78)",
      fontWeight: 500,
    },
    venueLine: {
      marginTop: 0,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    whenLine: {
      marginTop: theme.spacing(0.25),
    },
    fee: {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    formatLine: {
      marginTop: theme.spacing(1),
    },
    teeLine: {
      marginTop: theme.spacing(0.5),
    },
    teeLineFirst: {
      marginTop: theme.spacing(0.5),
    },
    dot: {
      display: "inline-block",
      width: 16,
      height: 4,
      margin: 0,
      padding: 0,
      border: 0,
      verticalAlign: "middle",
      position: "relative",
      fontSize: 4,
      lineHeight: 0,
      fontWeight: 400,
      overflow: "hidden",
      flexShrink: 0,
      "&::before": {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        width: 4,
        height: 4,
        marginTop: -2,
        marginLeft: -2,
        borderRadius: "50%",
        backgroundColor: "rgba(0, 0, 0, 0.22)",
      },
    },
  });

export const rangeGameListItemStyle = (isRange: boolean): CSSProperties | undefined =>
  isRange ? { backgroundColor: RANGE_GAME_COLORS.bg } : undefined;
